import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";

const TABS = [
  ["dashboard", "Dashboard"],
  ["queue", "Queue"],
  ["waiting_on_client", "Waiting on Client"],
  ["internal_review", "Internal Review"],
  ["client_review", "Client Review"],
  ["ready_to_file", "Ready to File"],
  ["completed", "Completed"],
];

// Fixed server-side filter applied for each preset tab.
const TAB_FILTER = {
  waiting_on_client: { waiting: "client" },
  internal_review: { stage: "internal_review" },
  client_review: { stage: "client_review" },
  ready_to_file: { stage: "ready_to_file" },
  completed: { stage: "completed" },
};

const WIDGETS = [
  ["production_ready", "Production Ready", "Inbox", "bg-slate-100 text-slate-600", { stage: "production_ready" }],
  ["assigned_today", "Assigned Today", "UserPlus", "bg-blue-100 text-blue-700", { stage: "assigned" }],
  ["in_preparation", "In Preparation", "PencilRuler", "bg-indigo-100 text-indigo-700", { stage: "in_preparation" }],
  ["waiting_on_client", "Waiting on Client", "UserRoundSearch", "bg-amber-100 text-amber-700", { waiting: "client" }],
  ["waiting_on_third_party", "Waiting on 3rd Party", "Building2", "bg-amber-100 text-amber-700", { waiting: "third_party" }],
  ["waiting_on_internal_review", "Waiting Internal", "Hourglass", "bg-amber-100 text-amber-700", { waiting: "internal_review" }],
  ["internal_review", "Internal Review", "ScanSearch", "bg-violet-100 text-violet-700", { stage: "internal_review" }],
  ["corrections", "Corrections", "RotateCcw", "bg-orange-100 text-orange-700", { stage: "corrections" }],
  ["client_review", "Client Review", "MessagesSquare", "bg-cyan-100 text-cyan-700", { stage: "client_review" }],
  ["ready_to_file", "Ready to File", "Send", "bg-teal-100 text-teal-700", { stage: "ready_to_file" }],
  ["filed", "Filed / Delivered", "FileCheck2", "bg-emerald-100 text-emerald-700", { stage: "filed" }],
  ["completed", "Completed", "CheckCircle2", "bg-emerald-100 text-emerald-700", { stage: "completed" }],
];
const FLAGS = [
  ["overdue", "Overdue", "AlarmClock", "bg-rose-100 text-rose-700", {}],
  ["high_priority", "High Priority", "Flame", "bg-rose-100 text-rose-700", { priority: "high" }],
  ["high_complexity", "High Complexity", "Layers", "bg-purple-100 text-purple-700", {}],
];

const STAGE_TONE = {
  production_ready: "bg-slate-100 text-slate-600", assigned: "bg-blue-100 text-blue-700", in_preparation: "bg-indigo-100 text-indigo-700",
  internal_review: "bg-violet-100 text-violet-700", corrections: "bg-orange-100 text-orange-700", final_review: "bg-violet-100 text-violet-700",
  client_review: "bg-cyan-100 text-cyan-700", ready_to_file: "bg-teal-100 text-teal-700", filed: "bg-emerald-100 text-emerald-700", completed: "bg-emerald-100 text-emerald-700",
};
const PRESETS = [
  ["", "All Active"], ["waiting_on_client", "Waiting on Client"], ["internal_review", "Internal Review"],
  ["client_review", "Client Review"], ["ready_to_file", "Ready to File"], ["completed", "Completed"],
];

export default function Production() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "dashboard";

  const goTab = (t) => navigate(`/production?tab=${t}`);

  return (
    <div className="p-6 max-w-[1500px] mx-auto space-y-5" data-testid="production">
      <div>
        <p className="text-xs text-slate-400 uppercase tracking-wide">Production Department</p>
        <h1 className="text-2xl font-bold text-slate-900">Production</h1>
        <p className="text-sm text-slate-500 mt-0.5">Preparation, review, quality control, filing and delivery — from Production Ready through completion.</p>
      </div>

      {/* Tab bar */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200 pb-px" data-testid="production-tabs">
        {TABS.map(([key, label]) => (
          <button key={key} onClick={() => goTab(key)} data-testid={`production-tab-${key}`}
            className={`text-sm px-3.5 py-2 rounded-t-lg border-b-2 -mb-px transition-colors ${tab === key ? "border-blue-600 text-blue-700 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{label}</button>
        ))}
      </div>

      {tab === "dashboard"
        ? <DashboardView navigate={navigate} />
        : <QueueView navigate={navigate} sp={sp} setSp={setSp} tab={tab} />}
    </div>
  );
}

function DashboardView({ navigate }) {
  const [w, setW] = useState(null);
  useEffect(() => { api.get("/production/dashboard").then((r) => setW(r.data.widgets)).catch(() => setW({})); }, []);

  const go = (params) => navigate(`/production?${new URLSearchParams({ tab: "queue", ...params })}`);

  return (
    <div className="space-y-6" data-testid="production-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {WIDGETS.map(([key, label, icon, tone, params]) => {
          const Ic = Icons[icon];
          return (
            <button key={key} onClick={() => go(params)} data-testid={`prod-widget-${key}`}
              className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center gap-3 hover:border-blue-400 transition-colors text-left">
              <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tone}`}><Ic className="h-5 w-5" /></div>
              <div><div className="text-2xl font-bold text-slate-900 leading-none">{w ? (w[key] ?? 0) : "—"}</div><div className="text-xs text-slate-500 mt-1">{label}</div></div>
            </button>
          );
        })}
      </div>

      <div>
        <h2 className="text-sm font-semibold text-slate-500 mb-2">Attention</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {FLAGS.map(([key, label, icon, tone, params]) => {
            const Ic = Icons[icon];
            return (
              <button key={key} onClick={() => go(params)} data-testid={`prod-flag-${key}`}
                className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center gap-3 hover:border-rose-300 transition-colors text-left">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tone}`}><Ic className="h-5 w-5" /></div>
                <div><div className="text-2xl font-bold text-slate-900 leading-none">{w ? (w[key] ?? 0) : "—"}</div><div className="text-xs text-slate-500 mt-1">{label}</div></div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function QueueView({ navigate, sp, setSp, tab }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const fixed = TAB_FILTER[tab] || null; // preset tabs lock the filter
  const isGenericQueue = tab === "queue";

  const stage = fixed?.stage || (isGenericQueue ? sp.get("stage") || "" : "");
  const waiting = fixed?.waiting || (isGenericQueue ? sp.get("waiting") || "" : "");
  const priority = isGenericQueue ? sp.get("priority") || "" : "";

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (stage) params.set("stage", stage);
    if (waiting) params.set("waiting", waiting);
    if (priority) params.set("priority", priority);
    if (q) params.set("q", q);
    api.get(`/production/records?${params}`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [stage, waiting, priority, q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const applyPreset = (val) => {
    const next = { tab: "queue" };
    if (val === "waiting_on_client") next.waiting = "client";
    else if (val) next.stage = val;
    setSp(next);
  };
  const activePreset = waiting === "client" ? "waiting_on_client" : stage;

  return (
    <div className="space-y-5" data-testid="production-queue">
      <div className="flex flex-wrap items-center gap-2">
        {isGenericQueue && PRESETS.map(([val, label]) => (
          <button key={label} onClick={() => applyPreset(val)} data-testid={`prod-preset-${val || "all"}`}
            className={`text-sm px-3 py-1.5 rounded-lg border ${activePreset === val ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{label}</button>
        ))}
        <div className="relative flex-1 min-w-[200px]">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="prod-search" placeholder="Search client or engagement…" className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap" data-testid="prod-table">
          <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
            {["Client", "Engagement", "Service", "Year", "Stage", "Preparer", "Reviewer", "Priority", "Cplx", "Days", "Open IRs", ""].map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {items === null ? <tr><td colSpan={12} className="px-5 py-12 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500 inline" /></td></tr> :
            items.length === 0 ? <tr><td colSpan={12} className="px-5 py-12 text-center text-slate-400">No engagements in this view.</td></tr> :
            items.map((r) => (
              <tr key={r.id} onClick={() => navigate(`/production/${r.id}`)} data-testid={`prod-row-${r.id}`} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer">
                <td className="px-3 py-2.5"><div className="font-medium text-slate-800">{r.client_name || "—"}</div><div className="text-xs text-slate-400">{r.client_number}</div></td>
                <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{r.engagement_number}</td>
                <td className="px-3 py-2.5 text-slate-600 capitalize">{(r.service_type || "—").replace(/_/g, " ")}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.filing_year || "—"}</td>
                <td className="px-3 py-2.5"><span className={`text-[11px] px-2 py-0.5 rounded-full ${STAGE_TONE[r.stage] || "bg-slate-100"}`}>{r.waiting_label || r.stage_label}</span></td>
                <td className="px-3 py-2.5 text-slate-600">{r.preparer}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.reviewer}</td>
                <td className="px-3 py-2.5"><span className={`text-[11px] capitalize ${r.priority === "high" ? "text-rose-600 font-semibold" : "text-slate-500"}`}>{r.priority}</span></td>
                <td className="px-3 py-2.5 capitalize text-slate-500">{r.complexity_label}</td>
                <td className="px-3 py-2.5 text-slate-500">{r.days_in_stage}</td>
                <td className="px-3 py-2.5">{r.open_info_requests > 0 ? <span className="text-amber-600 font-semibold">{r.open_info_requests}</span> : <span className="text-slate-300">0</span>}</td>
                <td className="px-3 py-2.5 text-right"><Icons.ChevronRight className="h-4 w-4 text-slate-300 inline" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
